/**
 * ThemePlayer — pílula "Ouça o tema do ..." (handoff "Player Tema Copa").
 *
 * Casca visual do player; todo o estado (play/pause/volume) vem do
 * CopaThemePlayerProvider, que é compartilhado entre o cabeçalho da Convocação
 * (onboarding) e o cabeçalho do Álbum.
 *
 * A mesma trilha é o tema institucional do Joga Junto BB, então a pílula também
 * aparece fora da campanha (página Manifesto). Por isso `themeName` parametriza
 * como o tema é nomeado nos rótulos, e `telemetry` desliga os eventos
 * `copa_interaction` fora da Copa — senão o relatório da campanha passaria a
 * contar interações que não aconteceram nela.
 */

import React, { useState } from 'react';
import { registerInteractionCampaign } from 'services/actionlog.service';
import { useCopaThemePlayer } from '../../CopaThemePlayerContext';
import { TranscriptionModal } from './TranscriptionModal';
import styles from './ThemePlayer.module.scss';

/** Nome do tema nas telas da campanha — mantém os rótulos originais da Copa. */
const DEFAULT_THEME_NAME = 'Álbum BB 2026';

function PlayIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
      <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
    </svg>
  );
}

function MusicNoteIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="#fbfd00" aria-hidden="true">
      <path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3z" />
    </svg>
  );
}

function VolumeIcon({ muted }: { muted?: boolean }) {
  // No zero, mostra o alto-falante "desabilitado" (com X) em tom mais apagado.
  if (muted) {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M3 9v6h4l5 5V4L7 9H3z" fill="#5f748f" />
        <path d="M15 9.5l5 5M20 9.5l-5 5" stroke="#5f748f" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="#9fb6d6" aria-hidden="true">
      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3a4.5 4.5 0 0 0-2.5-4.03v8.05A4.5 4.5 0 0 0 16.5 12z" />
    </svg>
  );
}

/** Alto-falante silenciado (mute) — exibido em vermelho quando o tema está mudo. */
function VolumeMutedIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="#ff5a5a" aria-hidden="true">
      <path d="M3 9v6h4l5 5V4L7 9H3z" />
      <path d="M22 9.41 20.59 8 18 10.59 15.41 8 14 9.41 16.59 12 14 14.59 15.41 16 18 13.41 20.59 16 22 14.59 19.41 12z" />
    </svg>
  );
}

/** Seta para baixo + bandeja — ação de baixar a trilha. */
function DownloadIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#9fb6d6"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3v11m0 0-4-4m4 4 4-4" />
      <path d="M5 17v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2" />
    </svg>
  );
}

interface ThemePlayerProps {
  className?: string;
  /** Como o tema é nomeado nos rótulos e leitores de tela. */
  themeName?: string;
  /** Registra as interações no relatório da Copa. Desligue fora da campanha. */
  telemetry?: boolean;
}

export function ThemePlayer({
  className,
  themeName = DEFAULT_THEME_NAME,
  telemetry = true,
}: ThemePlayerProps) {
  const { src, playing, volume, muted, toggle, toggleMute, setVolume } = useCopaThemePlayer();
  const [showTranscription, setShowTranscription] = useState(false);

  return (
    <div className={`${styles.player} ${className ?? ''}`}>
      <button
        type="button"
        className={styles.playBtn}
        onClick={toggle}
        aria-pressed={playing}
        aria-label={playing ? `Pausar o tema do ${themeName}` : `Tocar o tema do ${themeName}`}
      >
        {playing ? <PauseIcon /> : <PlayIcon />}
      </button>

      <div className={styles.labels}>
        <div className={styles.titleRow}>
          <MusicNoteIcon />
          <span className={styles.title}>Ouça o tema do {themeName}</span>
        </div>
        <div className={styles.subtitleRow}>
          <span className={styles.subtitle}>Joga Junto BB</span>
          <button
            type="button"
            className={styles.transcriptionBtn}
            onClick={() => setShowTranscription(true)}
            aria-label={`Ver a transcrição da letra do tema do ${themeName}`}
          >
            Transcrição
          </button>
        </div>
      </div>

      <div className={styles.volumeGroup}>
        <button
          type="button"
          className={`${styles.muteBtn} ${muted ? styles.muteBtnActive : ''}`}
          onClick={toggleMute}
          aria-pressed={muted}
          aria-label={
            muted ? `Ativar o som do tema do ${themeName}` : `Silenciar o tema do ${themeName}`
          }
          title={muted ? 'Ativar som' : 'Silenciar'}
        >
          {muted ? <VolumeMutedIcon /> : <VolumeIcon />}
        </button>
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(volume * 100)}
          onChange={e => setVolume(Number(e.target.value) / 100)}
          className={styles.volumeSlider}
          aria-label={`Volume do tema do ${themeName}`}
        />
      </div>

      <div className={styles.downloadGroup}>
        <a
          href={src}
          download="Tema Album BB 2026 - Joga Junto BB.mp3"
          className={styles.downloadBtn}
          onClick={() => {
            if (telemetry) {
              registerInteractionCampaign('me', 'copa_interaction', { type: 'download_theme_mp3' });
            }
          }}
          aria-label={`Baixar o tema do ${themeName}`}
          title="Baixar o tema"
        >
          <DownloadIcon />
        </a>
      </div>

      <TranscriptionModal
        isOpen={showTranscription}
        onClose={() => setShowTranscription(false)}
        themeName={themeName}
        telemetry={telemetry}
      />
    </div>
  );
}
